/**
 * Shipping rate model — simplified. Analyzing the legacy `courier_settings.json`
 * showed every fee decomposes as `package_base_rate × zone_multiplier[origin][dest]`,
 * and the multiplier is ~constant across package sizes. So instead of a full
 * courier×origin×destination×package matrix, we store:
 *
 *   - per courier: a list of PACKAGES (pouch/box) with dimensions + ONE base rate,
 *   - a single shared 4×4 `routeMultipliers` grid (origin zone → dest zone).
 *
 * fee = package.baseRate × routeMultipliers[originZone][destZone].
 *
 * No weight: the package is chosen by DIMENSIONS only (smallest box the cart's
 * bounding dimensions fit into). Stored as the `config` jsonb of a
 * `shipping_configs` row (highest version is active); the admin Shipping editor
 * edits these and publishes a new version. No courier IDs are hard-coded.
 */

/** A zone, used for both origin (POO branch) and destination (member). */
export type ShippingZone = {
  id: string;
  label: string;
  /** PH region codes that resolve to this zone (e.g. "13" = Metro Manila). */
  regCodes: string[];
};

/** A pouch/box size offered by a courier. Chosen by dimension fit; no weight. */
export type ShippingPackage = {
  key: string;
  label: string;
  /** Max dimensions the package holds. 0/undefined = no constraint (fits anything). */
  maxLengthCm?: number;
  maxWidthCm?: number;
  maxHeightCm?: number;
  /** Max total cart weight (kg) the package/courier carries. 0/undefined = no cap. */
  maxWeightKg?: number;
  /** Base fee at the reference (local) zone. Multiplied by the route factor. */
  baseRate: number;
};

export type ShippingCourier = {
  id: string;
  label: string;
  /** R2 object key for the courier logo (presigned on read); shown beside the name at checkout. */
  logoKey?: string;
  enabled: boolean;
  cod: boolean;
  /** Pickup-style courier (e.g. Branch Pickup): no packages, uses flatFee (often 0). */
  branchOnly: boolean;
  /** Optional simple flat fee — used when the courier has no packages. */
  flatFee?: number;
  packages: ShippingPackage[];
  /**
   * Courier tracking-link template. The order's tracking URL is built from this
   * + the entered tracking number when an admin ships, so staff only type the
   * number. Use `{tracking}` as the placeholder (e.g.
   * `https://courier.com/track?n={tracking}`); if absent, the number is appended.
   * Empty = no clickable tracking link for this courier.
   */
  trackingUrlTemplate?: string;
};

/**
 * Build an order tracking URL from a courier's template + the tracking number.
 * Returns null when there's no template or number. `{tracking}` is the
 * placeholder; with no placeholder the number is appended to the template.
 */
export function buildTrackingUrl(
  template: string | null | undefined,
  trackingNumber: string | null | undefined,
): string | null {
  const t = template?.trim();
  const n = trackingNumber?.trim();
  if (!t || !n) return null;
  const enc = encodeURIComponent(n);
  return t.includes('{tracking}') ? t.replaceAll('{tracking}', enc) : t + enc;
}

/**
 * Derive a courier's tracking-page landing URL from its `trackingUrlTemplate`,
 * without a specific tracking number — used by the storefront "Track Order" page
 * to link a shopper to the courier's own tracker. Drops the `{tracking}`
 * placeholder and any now-dangling trailing query fragment
 * (e.g. `.../track/?no={tracking}` → `.../track/`). Returns null with no template.
 */
export function courierTrackingHomeUrl(template: string | null | undefined): string | null {
  const t = template?.trim();
  if (!t) return null;
  let base = t.includes('{tracking}') ? t.slice(0, t.indexOf('{tracking}')) : t;
  // Remove a trailing, now-empty query param (`?no=` / `&id=`) then a bare `?`/`&`.
  base = base.replace(/[?&][^?&=]*=?$/, '').replace(/[?&]$/, '');
  return base || null;
}

export type ShippingConfig = {
  zones: ShippingZone[];
  /** routeMultipliers[originZoneId][destZoneId] → factor. Local route = 1.0. */
  routeMultipliers: Record<string, Record<string, number>>;
  couriers: ShippingCourier[];
};

/** A computed cart parcel: the bounding dimensions + total weight of the cart. */
export type CartParcel = {
  maxLengthCm: number;
  maxWidthCm: number;
  maxHeightCm: number;
  /** Σ qty × per-item weight (kg) across the cart. Checked against package caps. */
  totalWeightKg: number;
};

/** One courier's quote for a parcel + origin/destination. `fee` is a money string. */
export type ShippingQuote = {
  courierId: string;
  courierLabel: string;
  cod: boolean;
  branchOnly: boolean;
  /** The package size chosen, or null for flat-fee / pickup couriers. */
  packageKey: string | null;
  packageLabel: string | null;
  fee: string;
  available: boolean;
  reason?: string;
  /**
   * Set when the fee was zeroed by the per-product free-shipping tag (every
   * item in this store group is tagged). Lets the UI show a distinct "FREE
   * shipping" label vs an incidental ₱0 flat/pickup fee.
   */
  freeShipping?: boolean;
  /**
   * Set when there is nothing to deliver at all — every item in the group is a
   * virtual product/service. Distinct from `freeShipping`, which still ships a
   * real parcel at no charge.
   */
  virtual?: boolean;
};

/** Resolve which zone a PH `region_code` belongs to. Null if unmapped. */
export function regionForCode(
  config: ShippingConfig,
  regionCode: string | null | undefined,
): string | null {
  if (!regionCode) return null;
  const z = config.zones.find((zone) => zone.regCodes.includes(regionCode));
  return z?.id ?? null;
}

/** The route multiplier for origin→dest, defaulting to 1.0 when unset. */
export function routeMultiplier(config: ShippingConfig, origin: string, dest: string): number {
  return config.routeMultipliers?.[origin]?.[dest] ?? 1;
}

/** Does a parcel fit within a package's dimension + weight caps? (0/undefined = no cap). */
export function parcelFitsPackage(parcel: CartParcel, pkg: ShippingPackage): boolean {
  const okDim = (cap: number | undefined, val: number) => !cap || cap <= 0 || val <= cap;
  return (
    okDim(pkg.maxLengthCm, parcel.maxLengthCm) &&
    okDim(pkg.maxWidthCm, parcel.maxWidthCm) &&
    okDim(pkg.maxHeightCm, parcel.maxHeightCm) &&
    okDim(pkg.maxWeightKg, parcel.totalWeightKg)
  );
}

export const DEFAULT_SHIPPING_ZONES: ShippingZone[] = [
  { id: 'metro_manila', label: 'Metro Manila', regCodes: ['13'] },
  { id: 'luzon', label: 'Luzon', regCodes: ['01', '02', '03', '04', '05', '14', '17'] },
  { id: 'visayas', label: 'Visayas', regCodes: ['06', '07', '08'] },
  { id: 'mindanao', label: 'Mindanao', regCodes: ['09', '10', '11', '12', '15', '16'] },
];

/**
 * Default origin→destination zone multipliers, derived from the legacy LBC
 * kilobox ratios: same-zone ≈ 1.0, MM↔Luzon ≈ 1.2, inter-island ≈ 1.33–1.36.
 * Admin-editable; shared across couriers.
 */
export const DEFAULT_ROUTE_MULTIPLIERS: Record<string, Record<string, number>> = {
  metro_manila: { metro_manila: 1.0, luzon: 1.2, visayas: 1.33, mindanao: 1.33 },
  luzon: { metro_manila: 1.2, luzon: 1.2, visayas: 1.36, mindanao: 1.36 },
  visayas: { metro_manila: 1.33, luzon: 1.36, visayas: 1.0, mindanao: 1.36 },
  mindanao: { metro_manila: 1.33, luzon: 1.36, visayas: 1.36, mindanao: 1.0 },
};
